// src/features/chat/services/assistantClient.js

import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../firebase';

// Callable with explicit timeout to mirror backend limits
const assistantChatCallable = httpsCallable(functions, 'assistantChat', {
  timeout: 240000,
});
const assistantSurfaceOpenedCallable = httpsCallable(functions, 'assistant_surface_opened', {
  timeout: 30000,
});
const assistantPrimitiveSelectedCallable = httpsCallable(
  functions,
  'assistant_interaction_primitive_selected',
  {
    timeout: 30000,
  }
);
const assistantUndoEffectReceiptCallable = httpsCallable(
  functions,
  'assistant_undo_effect_receipt',
  {
    timeout: 30000,
  }
);
const assistantApproveApprovalRequestCallable = httpsCallable(
  functions,
  'assistant_approve_approval_request',
  {
    timeout: 30000,
  }
);
const assistantRejectApprovalRequestCallable = httpsCallable(
  functions,
  'assistant_reject_approval_request',
  {
    timeout: 30000,
  }
);

/**
 * Normalize image payloads for the assistant.
 * @param {Array} images
 * @returns {Array}
 */
export function normalizeAssistantImages(images = []) {
  if (!Array.isArray(images)) return [];

  return images
    .map((img) => {
      if (!img?.url) return null;
      const mimeType = img.mimeType || img.type;
      const normalized = mimeType ? { url: img.url, mimeType } : { url: img.url };
      if (typeof img.name === 'string' && img.name.trim()) {
        normalized.name = img.name.trim();
      }
      return normalized;
    })
    .filter(Boolean);
}

/**
 * Build optional context payload for locale/timezone hints.
 * @param {Object} options
 * @param {string} [options.locale]
 * @param {string} [options.timezone]
 * @returns {Object|undefined}
 */
export function buildAssistantContext({ locale, timezone } = {}) {
  const context = {};
  if (typeof locale === 'string' && locale.trim()) {
    context.locale = locale.trim();
  }
  if (typeof timezone === 'string' && timezone.trim()) {
    context.timezone = timezone.trim();
  }
  return Object.keys(context).length ? context : undefined;
}

/**
 * Build the assistant_surface_opened payload for Phase 2 primitives.
 * @param {Object} [options]
 * @param {string} [options.locale]
 * @param {string} [options.timezone]
 * @param {{ type?: string, id?: string }|undefined} [options.anchorHint]
 * @returns {Object}
 */
export function buildAssistantSurfaceOpenedPayload({ locale, timezone, anchorHint } = {}) {
  const payload = {
    surface: 'private_user',
    threadHint: 'private',
    anchorHint: anchorHint || { type: 'composer' },
  };
  const context = buildAssistantContext({ locale, timezone });
  if (context) {
    payload.context = context;
  }
  return payload;
}

/**
 * Whether the Phase 2 assistant_surface_opened trigger is enabled on the client.
 * @returns {boolean}
 */
export function isAssistantSurfaceOpenedEnabled() {
  return String(process.env.REACT_APP_ASSISTANT_SURFACE_OPENED_ENABLED || '').toLowerCase() === 'true';
}

/**
 * Parse error from Firebase Functions for user-friendly display.
 * @param {Error} error
 * @returns {{ type: string, message: string, resetAt: Date|null }}
 */
export function parseAssistantError(error) {
  const code = error?.code?.replace('functions/', '') || '';
  const details = error?.details || {};

  if (code === 'failed-precondition' && details.reason === 'assistant-app-check-required') {
    return {
      type: 'app_check',
      message: 'App verification failed. Refresh and try again.',
      resetAt: null,
    };
  }

  if (code === 'resource-exhausted') {
    return {
      type: 'rate_limit',
      message: 'Rate limit reached. Please wait before sending another message.',
      resetAt: details.resetAt ? new Date(details.resetAt) : null,
    };
  }

  if (code === 'unauthenticated') {
    return {
      type: 'auth',
      message: 'Please sign in to use the assistant.',
      resetAt: null,
    };
  }

  if (code === 'permission-denied') {
    return {
      type: 'permission',
      message: 'You do not have permission to access this chat.',
      resetAt: null,
    };
  }

  if (code === 'unavailable' && details.reason === 'private-streaming-required') {
    return {
      type: 'streaming_unavailable',
      message: 'Assistant streaming is unavailable. Refresh and try again.',
      resetAt: null,
    };
  }

  return {
    type: 'error',
    message: error?.message || 'Something went wrong. Please try again.',
    resetAt: null,
  };
}

/**
 * Normalize assistant callable responses while preserving unknown runtime fields.
 * @param {Object} response
 * @returns {Object}
 */
export function normalizeAssistantResponse(response) {
  const data = response && typeof response === 'object' ? response : {};
  const runtimeData = { ...data };
  delete runtimeData.citedRecords;
  return {
    ...runtimeData,
    clientSendId: typeof data.clientSendId === 'string' ? data.clientSendId : undefined,
    messageId: typeof data.messageId === 'string' ? data.messageId : undefined,
    messageCreatedAt: typeof data.messageCreatedAt === 'string' ? data.messageCreatedAt : undefined,
    text: typeof data.text === 'string' ? data.text : '',
    recordCitations: Array.isArray(data.recordCitations) ? data.recordCitations : undefined,
    draft: data.draft ?? undefined,
  };
}

/**
 * Stream the assistant response with optional chunk handler.
 * Falls back to non-streaming callable for group sends only.
 * @param {Object} payload
 * @param {Object} [options]
 * @param {Function} [options.onChunk] - Callback for streaming chunks
 * @param {AbortSignal} [options.signal] - Abort signal for cancelling the stream
 * @returns {Promise<Object>} Final assistant response
 */
export async function streamAssistantChat(payload, { onChunk, signal } = {}) {
  // Private sends require message_committed before persisted assistant replies
  // can reconcile with pending UI, so final-only callable fallback is unsafe.
  if (typeof assistantChatCallable.stream !== 'function') {
    if (payload?.chatKind === 'private') {
      const error = new Error(
        'Private assistant sends require streaming callable support.'
      );
      error.code = 'functions/unavailable';
      error.details = { reason: 'private-streaming-required' };
      throw error;
    }
    const result = await assistantChatCallable(payload);
    return normalizeAssistantResponse(result.data);
  }

  const streamResult = await assistantChatCallable.stream(payload, { signal });
  const { data: responsePromise, stream } = streamResult;

  let streamError = null;

  const reader = (async () => {
    try {
      for await (const chunk of stream) {
        if (onChunk) {
          onChunk(chunk);
        }
      }
    } catch (err) {
      streamError = err;
    }
  })();

  let response;
  let responseError = null;
  try {
    response = normalizeAssistantResponse(await responsePromise);
  } catch (err) {
    responseError = err;
  }
  await reader;

  if (responseError) {
    throw responseError;
  }

  if (streamError && streamError.name !== 'AbortError') {
    throw streamError;
  }

  return response;
}

/**
 * Fire-and-forget no-op surface-open trigger for the Zero-effort inbox shell.
 * @param {Object} payload
 * @returns {Promise<Object>}
 */
export async function notifyAssistantSurfaceOpened(payload) {
  const result = await assistantSurfaceOpenedCallable(payload || {});
  const data = result.data;
  if (data?.ok === false) {
    const error = new Error(
      'Assistant suggestions are refreshing.'
    );
    error.code = 'functions/unavailable';
    error.details = data;
    throw error;
  }
  return data;
}

/**
 * Create a server-owned primitive selection token.
 * @param {string} primitiveId
 * @returns {Promise<{ selectionId: string, expiresAt: string }>}
 */
export async function selectAssistantInteractionPrimitive(primitiveId) {
  const result = await assistantPrimitiveSelectedCallable({ primitiveId });
  const data = result.data || {};
  if (typeof data.selectionId !== 'string' || !data.selectionId) {
    throw new Error('Assistant suggestion could not be selected.');
  }
  return {
    selectionId: data.selectionId,
    expiresAt: typeof data.expiresAt === 'string' ? data.expiresAt : null,
  };
}

/**
 * Undo a still-eligible assistant-created Record receipt.
 * @param {string} receiptId
 * @returns {Promise<Object>}
 */
export async function undoAssistantEffectReceipt(receiptId) {
  const result = await assistantUndoEffectReceiptCallable({ receiptId });
  return result.data || {};
}

/**
 * Approve a pending Records harness approval request.
 * @param {string} approvalId
 * @returns {Promise<Object>}
 */
export async function approveAssistantApprovalRequest(approvalId) {
  const result = await assistantApproveApprovalRequestCallable({ approvalId });
  return result.data || {};
}

/**
 * Reject a pending Records harness approval request.
 * @param {string} approvalId
 * @returns {Promise<Object>}
 */
export async function rejectAssistantApprovalRequest(approvalId) {
  const result = await assistantRejectApprovalRequestCallable({ approvalId });
  return result.data || {};
}
